import { CSSProperties } from "react";

import { useIntl } from "react-intl";
import {
    UserSegmentTypes,
    useUserSegments
} from "src/uniform/components/personalization-container/src/use-user-segments";

import UFOSegment from "@atlaskit/react-ufo/segment";

import { useLocalizedHref } from "@/libraries/web-framework";
import { LogoV3 } from "@/libraries/wpl/foundations/src";
import { ProductPageSubNav } from "@/patterns/global-nav/src";
import { DropdownContent } from "@/templates/software/jira/service-management/jira-service-management-software-page/src/sections/software-jira-service-management-navbarv2";
import { useExperiment, experiments } from "@/utils/experiment";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";
import { useLoggedInUser } from "@/utils/logged-in-user-provider";

// eslint-disable-next-line complexity
export const RovoNav = () => {
    const intl = useIntl();

    const navLinks = [
        {
            globalLinksKey: "features",
            linkText: intl.formatMessage({
                id: "RovoPageTemplate.navbar.features",
                defaultMessage: "Features"
            }),
            hasSubnav: false,
            productKey: "rovo",
            href: useLocalizedHref("/software/rovo/features")
        },
        {
            globalLinksKey: "connectors",
            linkText: intl.formatMessage({
                id: "RovoPageTemplate.navbar.connectors",
                defaultMessage: "Connectors"
            }),
            hasSubnav: false,
            productKey: "rovo",
            href: useLocalizedHref("/software/rovo/connectors")
        },
        {
            globalLinksKey: "use-cases",
            linkText: intl.formatMessage({
                id: "RovoPageTemplate.navbar.use-cases",
                defaultMessage: "Use cases"
            }),
            hasSubnav: false,
            productKey: "rovo",
            href: useLocalizedHref("/software/rovo/use-cases")
        },
        {
            globalLinksKey: "resources",
            linkText: intl.formatMessage({
                id: "RovoPageTemplate.navbar.resources",
                defaultMessage: "Resources"
            }),
            hasSubnav: true,
            productKey: "rovo",
            containerPosition: "relative" as CSSProperties["position"],
            content: (
                <DropdownContent
                    orientation="column"
                    contentSections={[
                        {
                            links: [
                                {
                                    text: intl.formatMessage({
                                        id: "RovoPageTemplate.navbar.resources.ai-adoption-resources",
                                        defaultMessage: "AI adoption resources"
                                    }),
                                    href: useLocalizedHref(
                                        "http://community.atlassian.com/learning/hub/rovo"
                                    )
                                },
                                {
                                    text: intl.formatMessage({
                                        id: "RovoPageTemplate.navbar.resources.admin-onboarding",
                                        defaultMessage: "Admin onboarding"
                                    }),
                                    href: useLocalizedHref(
                                        "/software/rovo/guides/admin-guide"
                                    )
                                },
                                {
                                    text: intl.formatMessage({
                                        id: "RovoPageTemplate.navbar.resources.end-user-onboarding",
                                        defaultMessage: "End-user onboarding"
                                    }),
                                    href: useLocalizedHref(
                                        "/software/rovo/guides/end-user-guide"
                                    )
                                }
                            ]
                        }
                    ]}
                />
            )
        },
        {
            globalLinksKey: "licensing",
            linkText: intl.formatMessage({
                id: "RovoPageTemplate.navbar.licensing",
                defaultMessage: "Licensing"
            }),
            hasSubnav: false,
            productKey: "rovo",
            href: useLocalizedHref("/licensing/rovo")
        }
    ];

    const { isUserLoggedIn, requestInProgress } = useLoggedInUser();

    const isRovoCtasEnabled = checkFeatureFlagGate(
        featureFlags.gwp8011RovoProductTour
    );

    const gwp8979RovoOnWacButtonEG = useExperiment(
        experiments.gwp8979RovoChatOnWac
    );
    const gwp8979RovoOnWacButtonFG = checkFeatureFlagGate(
        featureFlags.gwp8979RovoOnWacButton
    );

    const { data: userSegments } = useUserSegments();

    const isRovoInPlanList =
        userSegments?.[UserSegmentTypes.OWNED_PRODUCTS]?.includes(
            "rovo.ondemand"
        );

    const defaultCTAUrl = useLocalizedHref(
        "/try/cloud/signup?bundle=teamwork-collection&primaryCollectionProduct=rovo.ondemand&edition=premium"
    );

    const isUserLoggedInAndCtasEnabledAndInPlan =
        isUserLoggedIn && isRovoCtasEnabled && isRovoInPlanList;

    const isRovoEnabled =
        isUserLoggedInAndCtasEnabledAndInPlan ||
        (gwp8979RovoOnWacButtonEG.type === "enrolled" &&
            gwp8979RovoOnWacButtonEG.value);

    const ctaText = isRovoEnabled
        ? intl.formatMessage({
              id: "RovoPageTemplate.navbar.cta.try-rovo",
              defaultMessage: "Try Rovo"
          })
        : isRovoCtasEnabled
        ? intl.formatMessage({
              id: "RovoPageTemplate.navbar.cta.get-rovo",
              defaultMessage: "Get Rovo"
          })
        : undefined;

    const tryNowCTAUrl = isUserLoggedInAndCtasEnabledAndInPlan
        ? "https://home.atlassian.com/chat"
        : isRovoCtasEnabled
        ? defaultCTAUrl
        : undefined;

    const isShowTryNowLink =
        !requestInProgress && tryNowCTAUrl && ctaText ? true : false;

    const shouldShowCTAButtonVariant =
        gwp8979RovoOnWacButtonEG.type === "enrolled" &&
        gwp8979RovoOnWacButtonEG.value &&
        gwp8979RovoOnWacButtonFG;

    const buttonVariant = shouldShowCTAButtonVariant ? "secondary" : "primary";

    return (
        <UFOSegment name="RovoNav">
            <ProductPageSubNav
                productLogoUrl="/software/rovo"
                productLogo={
                    <LogoV3 brand="rovo" type="logo" appearance="brand" />
                }
                productLogoAriaLabel={intl.formatMessage({
                    id: "RovoPageTemplate.navbar.rovo-logo",
                    defaultMessage: "Rovo logo"
                })}
                mobileLogo={
                    <LogoV3 brand="rovo" type="logo" appearance="brand" />
                }
                isShowTryNowLink={isShowTryNowLink}
                isIncludeMobileCTA={isShowTryNowLink}
                navigationTrynowCtaText={isShowTryNowLink ? ctaText : undefined}
                navigationTrynowCtaUrl={
                    isShowTryNowLink ? tryNowCTAUrl : undefined
                }
                navigationTrynowCtaButtonVariant={buttonVariant}
                mobileCtaText={isShowTryNowLink ? ctaText : undefined}
                mobileCtaUrl={isShowTryNowLink ? tryNowCTAUrl : undefined}
                productPageNavLinks={navLinks}
                mobileLinksSections={[
                    {
                        mobileLinkKey: "mobile-rovo",
                        mobileLinksProps: navLinks
                    }
                ]}
            />
        </UFOSegment>
    );
};
